import { z } from 'zod';
import { subscriptionStatusSchema } from './entitlement.js';
import { localeCodeSchema } from './i18n.js';
import { ianaTimezoneSchema } from './location.js';
import { membershipStatusSchema } from './membership.js';
import { pageQuerySchema } from './http.js';
import { tenantNameSchema } from './signup.js';
import { tenantSlugSchema, tenantStatusSchema } from './tenant.js';
import { label } from './text-policy.js';
/** Must stay identical — members and order — to Prisma `PartnerStatus` (S46-f22b-TASK-005 test). */
export const PARTNER_STATUSES = ['ACTIVE', 'SUSPENDED', 'TERMINATED'];
export const partnerStatusSchema = z.enum(PARTNER_STATUSES);
/** Prisma `PartnerBillingModel`. */
export const PARTNER_BILLING_MODELS = ['WHOLESALE', 'REFERRAL'];
export const partnerBillingModelSchema = z.enum(PARTNER_BILLING_MODELS);
/** Prisma `PartnerSetupAccess`. ENABLED is the default when a tenant is linked (owner decision 3). */
export const PARTNER_SETUP_ACCESS_VALUES = ['ENABLED', 'REVOKED'];
export const partnerSetupAccessSchema = z.enum(PARTNER_SETUP_ACCESS_VALUES);
/** Outcome of one provider invoice billed to a partner. Lock-step with the Prisma enum `PartnerInvoiceStatus`. */
export const PARTNER_INVOICE_STATUSES = ['PAID', 'PAYMENT_FAILED', 'VOID'];
export const partnerInvoiceStatusSchema = z.enum(PARTNER_INVOICE_STATUSES);
/** What one line of a referral partner's commission ledger is. Must stay identical to the Prisma enum. */
export const PARTNER_COMMISSION_ENTRY_KINDS = ['ACCRUAL', 'REVERSAL'];
export const partnerCommissionEntryKindSchema = z.enum(PARTNER_COMMISSION_ENTRY_KINDS);
export const PARTNER_NAME_MAX_LENGTH = 120;
export const partnerNameSchema = label({ max: PARTNER_NAME_MAX_LENGTH, allowEmpty: false });
/** 1 bp = 0.01 %. 10 000 bp = 100 %. Integers only. */
export const basisPointsSchema = z.number().int().min(0).max(10_000);
export const partnerDTOSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    status: partnerStatusSchema,
    billingModel: partnerBillingModelSchema,
    discountBps: basisPointsSchema.nullable(),
    commissionBps: basisPointsSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * POST /api/web/partners (operator). Terms rule: WHOLESALE ⇒ discountBps set, commissionBps null;
 * REFERRAL ⇒ commissionBps set, discountBps null. Violations are field issues on the offending
 * key with message 'PARTNER_TERMS_REQUIRED' (missing) or 'PARTNER_TERMS_NOT_APPLICABLE' (present
 * but wrong model). status is not an input: a new partner is ACTIVE.
 */
export const createPartnerSchema = z
    .object({
    name: partnerNameSchema,
    billingModel: partnerBillingModelSchema,
    discountBps: basisPointsSchema.nullable(),
    commissionBps: basisPointsSchema.nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    const applicable = value.billingModel === 'WHOLESALE' ? 'discountBps' : 'commissionBps';
    const other = value.billingModel === 'WHOLESALE' ? 'commissionBps' : 'discountBps';
    if (value[applicable] === null) {
        ctx.addIssue({ code: 'custom', path: [applicable], message: 'PARTNER_TERMS_REQUIRED' });
    }
    if (value[other] !== null) {
        ctx.addIssue({ code: 'custom', path: [other], message: 'PARTNER_TERMS_NOT_APPLICABLE' });
    }
});
/**
 * PATCH /api/web/partners/:partnerId (operator). Exactly ONE rate, and it must be the partner's own
 * model's — the service refuses the other with a field issue 'PARTNER_TERMS_NOT_APPLICABLE'.
 * `billingModel`, `name` and `status` are not inputs (decision W14).
 */
export const updatePartnerTermsSchema = z
    .object({
    discountBps: basisPointsSchema.optional(),
    commissionBps: basisPointsSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    const given = [value.discountBps, value.commissionBps].filter((v) => v !== undefined).length;
    if (given !== 1)
        ctx.addIssue({ code: 'custom', path: ['discountBps'], message: 'PARTNER_TERMS_REQUIRED' });
});
export const partnerListQuerySchema = pageQuerySchema; // ordered by name asc, then id
/** One customer linked to a partner, as the OPERATOR sees it. Metadata only. */
export const partnerCustomerDTOSchema = z.object({
    tenantId: z.string().min(1),
    tenantSlug: z.string().min(1),
    tenantName: z.string().min(1),
    tenantStatus: tenantStatusSchema,
    setupAccess: partnerSetupAccessSchema,
    /** True when the partner, not the customer, pays for this account (wholesale). Account metadata — no amount, no rate. */
    billedToPartner: z.boolean(),
    linkedAt: z.string().datetime(),
});
export const partnerCustomerListQuerySchema = pageQuerySchema; // ordered by tenant name asc, then tenantId
/**
 * `GET /api/web/partner/customers` row — the PARTNER's own view of one linked customer
 * (adr/0035 decision 8 as amended 2026-10-11, owner decision 1). METADATA ONLY — no visitor
 * field, ever; a new field here is a new cross-tenant disclosure and needs an ADR amendment.
 */
export const partnerPortalCustomerDTOSchema = z.object({
    tenantId: z.string().min(1),
    tenantName: z.string().min(1),
    tenantStatus: tenantStatusSchema,
    /** The current non-canceled visitor PLAN subscription's plan name; null when the tenant has none. */
    planName: z.string().min(1).nullable(),
    planStatus: subscriptionStatusSchema.nullable(),
    locationCount: z.number().int().min(0),
    /** ACTIVE paired devices. */
    kioskCount: z.number().int().min(0),
    setupAccess: partnerSetupAccessSchema,
    /** False until somebody at the customer has joined (accepted an invitation or signed up). Account metadata — no member identity. */
    ownerJoined: z.boolean(),
    /** True when the partner, not the customer, pays for this account (wholesale). Account metadata — no amount, no rate. */
    billedToPartner: z.boolean(),
    linkedAt: z.string().datetime(),
});
export const partnerPortalCustomerListQuerySchema = pageQuerySchema; // ordered by tenant name asc, then tenantId
/**
 * `GET /api/web/partner/activity` row (adr/0035 decision 8, amended by S46-f22b-TASK-066). METADATA
 * ONLY: what the partner's own staff did, to which customer, when. No audit metadata, no target, no
 * customer member — a new field here is a new cross-tenant disclosure and needs an ADR amendment.
 */
export const partnerActivityDTOSchema = z.object({
    id: z.string().min(1),
    occurredAt: z.string().datetime(),
    /** `AuditEvent.action` — open vocabulary (`tenant.partner.customer_created`, `location.created`, …). */
    action: z.string().min(1),
    customerTenantId: z.string().min(1),
    customerName: z.string().min(1),
    /** The partner's OWN staff member who acted; null when that person is no longer on the team. */
    actorName: z.string().min(1).nullable(),
});
export const partnerActivityListQuerySchema = pageQuerySchema; // newest first, then id desc
/** `GET /api/web/partner/billing` — the partner's own commercial facts. Nothing about any one customer. */
export const partnerBillingSummaryDTOSchema = z.object({
    billingModel: partnerBillingModelSchema,
    /** The partner's wholesale discount; null for a REFERRAL partner. */
    discountBps: basisPointsSchema.nullable(),
    /** Customers this partner pays for (links with `billedToPartner`). */
    billedCustomerCount: z.number().int().min(0),
    /** False until the partner's billing account exists at the provider (first checkout, or first visit to the hosted portal). */
    hasBillingAccount: z.boolean(),
});
/**
 * `GET /api/web/partner/billing/invoices` row (adr/0035 decision 8, amended by S46-f22b-TASK-083):
 * one invoice billed to the partner for one customer. METADATA ONLY — an allow-list (S7): no
 * provider reference, no line items, no partner id. A new field here is a new disclosure and needs
 * an ADR amendment.
 */
export const partnerInvoiceDTOSchema = z.object({
    id: z.string().min(1),
    customerTenantId: z.string().min(1),
    customerName: z.string().min(1),
    status: partnerInvoiceStatusSchema,
    /** PAID: what was collected. PAYMENT_FAILED / VOID: what was due. Tax included. */
    amountMinorUnits: z.number().int().min(0),
    taxMinorUnits: z.number().int().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/),
    occurredAt: z.string().datetime(),
});
export const partnerInvoiceListQuerySchema = pageQuerySchema; // newest first (occurredAt desc, then id desc)
/** OPEN = the current UTC month, still accruing. DUE = a closed month not yet paid. PAID = settled. */
export const COMMISSION_PERIOD_STATUSES = ['OPEN', 'DUE', 'PAID'];
export const commissionPeriodStatusSchema = z.enum(COMMISSION_PERIOD_STATUSES);
/** A statement period: a UTC calendar month, `YYYY-MM`. */
export const commissionPeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/** `GET /api/web/partner/earnings` — the partner's own commercial facts. Nothing about any one customer. */
export const partnerEarningsSummaryDTOSchema = z.object({
    billingModel: partnerBillingModelSchema,
    /** The partner's referral commission; null for a WHOLESALE partner. */
    commissionBps: basisPointsSchema.nullable(),
});
/** One statement period of one currency. Amounts of different currencies are never added together. */
export const partnerCommissionPeriodDTOSchema = z.object({
    period: commissionPeriodSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** The period's own total. May be negative (a refund dated into a later month than its invoice). */
    amountMinorUnits: z.number().int(),
    status: commissionPeriodStatusSchema,
    /** When it was paid; null unless `status` is PAID. */
    paidAt: z.string().datetime().nullable(),
});
/** `GET /api/web/partners/:partnerId/commission-periods` — the partner-facing period plus what a settlement would pay. OPERATOR-ONLY. */
export const partnerCommissionPeriodAdminDTOSchema = partnerCommissionPeriodDTOSchema.extend({
    /** DUE rows only: everything unpaid in this currency up to and including this period — the amount
     *  marking it paid would pay out, and the `expectedAmountMinorUnits` the settlement request must send.
     *  May be zero or negative (nothing to pay yet). OPEN and PAID rows: null. */
    payableThroughMinorUnits: z.number().int().nullable(),
});
export const partnerCommissionPeriodListQuerySchema = pageQuerySchema; // newest period first, then currency A–Z
/**
 * `POST /api/web/partners/:partnerId/commission-settlements` — "this partner has been paid, in this
 * currency, through this period". `expectedAmountMinorUnits` is the `payableThroughMinorUnits` the
 * operator was shown: the server pays nothing if the figure has moved. STRICT: the partner is the
 * path's, the payer the verified operator, the amount and the date the server's — a body that names
 * any of them is refused, not ignored.
 */
export const settlePartnerCommissionSchema = z
    .object({
    currency: z.string().regex(/^[A-Z]{3}$/),
    throughPeriod: commissionPeriodSchema,
    expectedAmountMinorUnits: z.number().int().positive(),
    /** The operator's own payment reference (a transfer id). Letters, digits, space and `._#/-` only. */
    reference: z
        .string()
        .regex(/^[A-Za-z0-9 ._#/-]{1,64}$/)
        .optional(),
})
    .strict();
/** One settlement: what was paid, in which currency, through which month, and when. OPERATOR-ONLY. */
export const partnerCommissionSettlementDTOSchema = z.object({
    id: z.string().min(1),
    currency: z.string().regex(/^[A-Z]{3}$/),
    throughPeriod: commissionPeriodSchema,
    amountMinorUnits: z.number().int().positive(),
    paidAt: z.string().datetime(),
    reference: z.string().nullable(),
});
/**
 * One line of a statement (adr/0035 decision 8, amended by S46-f22b-TASK-099). METADATA ONLY — an
 * allow-list (S7): no invoice reference, no tax, no gross total, no plan, no person, no tenant id.
 * A new field here is a new disclosure and needs an ADR amendment.
 */
export const partnerCommissionLineDTOSchema = z.object({
    id: z.string().min(1),
    period: commissionPeriodSchema,
    kind: partnerCommissionEntryKindSchema,
    /** The customer's organization name as recorded when its invoice was paid. */
    customerName: z.string().min(1),
    occurredAt: z.string().datetime(),
    /** The commissionable amount this line adds (ACCRUAL) or takes back (REVERSAL): paid, less tax, less returns. Signed. */
    basisMinorUnits: z.number().int(),
    /** The rate that applied to the invoice when it was paid. */
    commissionBps: basisPointsSchema,
    /** The commission this line adds or takes back. Signed. */
    amountMinorUnits: z.number().int(),
    currency: z.string().regex(/^[A-Z]{3}$/),
});
/** `period` and `currency` are required: a statement is one month of one currency. Newest line first (occurredAt desc, then id desc). */
export const partnerCommissionLineListQuerySchema = pageQuerySchema.extend({
    period: commissionPeriodSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
});
/** POST /api/web/partners/:partnerId/customers (operator). */
export const linkPartnerCustomerSchema = z.object({ tenantSlug: tenantSlugSchema }).strict();
/** `POST /api/web/partner/customers` (adr/0035 decision 10). No partnerId, no status, no setupAccess: none is an input. */
export const createPartnerCustomerSchema = z
    .object({
    tenantName: tenantNameSchema,
    tenantSlug: tenantSlugSchema,
    ownerName: z.string().trim().min(1).max(120),
    ownerEmail: z.string().trim().email().max(320),
    /** The partner browser's IANA zone, for the auto-created HQ location only. Server defaults to UTC. */
    timezone: ianaTimezoneSchema.optional(),
})
    .strict();
/** `POST /api/web/partner/customers/:tenantId/owner-invite`. No role field: the invitee is always the first tenant.owner. */
export const partnerCustomerOwnerInviteSchema = z
    .object({ email: z.string().trim().email().max(320), name: z.string().trim().min(1).max(120) })
    .strict();
export const PARTNER_OWNER_INVITE_RESULTS = ['SENT', 'FAILED'];
export const partnerCustomerCreatedDTOSchema = z.object({
    tenantId: z.string().min(1),
    tenantSlug: z.string().min(1),
    tenantName: z.string().min(1),
    /** FAILED = the customer exists but the invitation email did not go out; re-send it (S46-f22b-TASK-057). */
    ownerInvite: z.enum(PARTNER_OWNER_INVITE_RESULTS),
});
/** GET|PATCH /api/web/tenant/partner — the customer's own "Your partner" card. */
export const tenantPartnerDTOSchema = z.object({
    partnerName: z.string().min(1),
    setupAccess: partnerSetupAccessSchema,
    linkedAt: z.string().datetime(),
    /** Null until the customer first changes setup access. */
    setupAccessChangedAt: z.string().datetime().nullable(),
});
/** GET answers { data: null } when the tenant has no partner. */
export const tenantPartnerResponseSchema = tenantPartnerDTOSchema.nullable();
export const updateTenantPartnerAccessSchema = z.object({ setupAccess: partnerSetupAccessSchema }).strict();
/** Wire copy of `PARTNER_ROLE_KEYS` (@instanexus/authz) — contracts may not import authz; a lock-step test in authz keeps them equal. */
export const PARTNER_ROLE_VALUES = ['partner.owner', 'partner.admin', 'partner.agent'];
export const partnerRoleSchema = z.enum(PARTNER_ROLE_VALUES);
/** adr/0035 decision 5. Lock-step with `PARTNER_SETUP_ROLE_KEYS` (@instanexus/authz) — tested there. */
export const PARTNER_SETUP_ROLE_VALUES = ['partner.setup', 'partner.setup-wholesale'];
export const partnerSetupRoleSchema = z.enum(PARTNER_SETUP_ROLE_VALUES);
/**
 * Which of OUR configured SPAs the "Open setup" link lands in (adr/0035 d.4 — the session and token
 * are identical either way; this only picks a base URL from validated config, never from the request).
 */
export const SETUP_DESTINATIONS = ['console', 'visitor'];
export const setupDestinationSchema = z.enum(SETUP_DESTINATIONS);
/** Body of `POST /api/web/partner/customers/:tenantId/setup-sessions`. Absent body ≡ `{}`. */
export const openSetupSessionSchema = z
    .object({ destination: setupDestinationSchema.default('console') })
    .strict();
/** `POST /api/web/partner/customers/:tenantId/setup-sessions` → 201. The code lives only in the fragment. */
export const partnerSetupHandoffDTOSchema = z.object({
    setupUrl: z.string().url(),
    handoffExpiresAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    roleKey: partnerSetupRoleSchema,
});
/** adr/0035 decision 4. The opaque setup-session bearer prefix; `authenticate` routes on it. */
export const SETUP_SESSION_TOKEN_PREFIX = 'pss_';
/** `POST /api/web/setup-sessions/exchange` (public). The single-use handoff code from the URL fragment. */
export const exchangeSetupHandoffSchema = z.object({ code: z.string().min(32).max(128) }).strict();
/** `POST /api/web/setup-sessions/exchange` → 200. Held in console memory only (no cookie, no refresh). */
export const setupSessionTokenDTOSchema = z.object({
    accessToken: z.string().startsWith(SETUP_SESSION_TOKEN_PREFIX).min(40),
    expiresAt: z.string().datetime(),
});
/** `GET /api/web/partner/auth/me` (adr/0035). No tenant fields: a partner principal has none. */
export const partnerMeDTOSchema = z.object({
    userId: z.string().min(1),
    email: z.string().email(),
    name: z.string().min(1),
    preferredLocale: localeCodeSchema,
    partnerId: z.string().min(1),
    partnerName: z.string().min(1),
    partnerRole: partnerRoleSchema,
    /** Sorted, de-duplicated; derived server-side from PARTNER_ROLES. A courtesy for the UI — the server refuses regardless. */
    permissions: z.array(z.string().min(1)),
    /** See `meDTOSchema.termsVersion`. */
    termsVersion: z.string().nullable().optional(),
});
/** `POST /api/web/partner/auth/login`. No tenantSlug: a partner principal has no tenant (adr/0035). */
export const partnerLoginRequestSchema = z.object({
    email: z.string().email().max(320),
    password: z.string().min(1).max(512),
});
/** `POST /api/web/partners/:partnerId/members` (operator). Phase 2 invites the partner OWNER only; no role field — the role is fixed server-side. */
export const invitePartnerMemberSchema = z.object({
    email: z.string().trim().email().max(320),
    name: z.string().trim().min(1).max(200),
});
/** A partner staff member (adr/0035). `id` is the `PartnerMembership.id`. */
export const partnerMemberDTOSchema = z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    email: z.string().email(),
    name: z.string().min(1),
    role: partnerRoleSchema,
    status: membershipStatusSchema,
    invitedAt: z.string().datetime(),
    acceptedAt: z.string().datetime().nullable(),
});
/** `GET /api/web/partner/team` (S46-f22b-TASK-063). */
export const partnerTeamListQuerySchema = pageQuerySchema; // ordered by member name asc, then membership id
/** `POST /api/web/partner/team`. The role is checked server-side against the inviter's own (no escalation). No partnerId: never an input. */
export const invitePartnerTeamMemberSchema = z
    .object({
    email: z.string().trim().email().max(320),
    name: z.string().trim().min(1).max(200),
    role: partnerRoleSchema,
})
    .strict();
/**
 * `PATCH /api/web/partner/team/:membershipId` (S46-f22b-TASK-064). Strict: the role and nothing
 * else — never a status, a partner or a user (S1, S4). The member is the path's target, looked up
 * under the verified principal's partner.
 */
export const updatePartnerTeamMemberSchema = z.object({ role: partnerRoleSchema }).strict();
//# sourceMappingURL=partner.js.map